/**
 * Bridge module: adapts Databender effects (which operate on raw 24-bit BMP
 * file bytes) to unstaglitch's canvas ImageData format.
 *
 * Databender effects encode the canvas to a real BMP, manipulate the raw
 * bytes directly (databending), then decode the result back to ImageData.
 * This produces authentic databending artifacts (row misalignment, header
 * corruption effects, byte-level glitches) that pixel-level manipulation
 * cannot replicate.
 *
 * Bridge flow:
 *   1. Encode canvas ImageData → 24-bit BMP bytes (Uint8Array)
 *   2. Run the effect's apply() on the BMP bytes
 *   3. Decode the (possibly resized) BMP bytes → ImageData
 *   4. Put ImageData back on the canvas
 *
 * Each bridged function has the signature: (canvas, ctx, ...args) => Promise<void>
 */

const { EFFECTS, EFFECT_MAP, getEffectIds, getDefaultParams } = require('./databender-effects');

// ---- BMP encode/decode (24-bit, bottom-up rows, 4-byte row padding) ----

function imageDataToBMP(imageData) {
    const w = imageData.width, h = imageData.height, data = imageData.data;
    const pad = (4 - ((w * 3) % 4)) % 4;
    const padded = w * 3 + pad;
    const size = 54 + padded * h;
    const b = new Uint8Array(size);
    const dv = new DataView(b.buffer);

    // BMP header (14 bytes)
    b[0] = 0x42; b[1] = 0x4D;           // 'BM'
    dv.setUint32(2, size, true);         // file size
    dv.setUint32(10, 54, true);          // data offset

    // DIB header (40 bytes, BITMAPINFOHEADER)
    dv.setUint32(14, 40, true);          // header size
    dv.setInt32(18, w, true);            // width
    dv.setInt32(22, h, true);            // height (positive = bottom-up)
    dv.setUint16(26, 1, true);           // planes
    dv.setUint16(28, 24, true);          // bpp
    dv.setUint32(30, 0, true);           // compression = BI_RGB
    dv.setUint32(34, padded * h, true);  // image size
    dv.setInt32(38, 2835, true);         // x ppm
    dv.setInt32(42, 2835, true);         // y ppm
    dv.setUint32(46, 0, true);           // colors used
    dv.setUint32(50, 0, true);           // important colors

    // Pixel data (BMP is bottom-up, BGR)
    for (let y = 0; y < h; y++) {
        const srcY = h - 1 - y;
        const row = 54 + y * padded;
        for (let x = 0; x < w; x++) {
            const i = (srcY * w + x) * 4, o = row + x * 3;
            b[o] = data[i + 2];     // B
            b[o + 1] = data[i + 1]; // G
            b[o + 2] = data[i];     // R
        }
        // padding bytes are already 0
    }

    return { bytes: b, width: w, height: h, dataOffset: 54, stride: padded };
}

function bmpToImageData(bytes) {
    // Parse BMP header
    if (bytes.length < 54 || bytes[0] !== 0x42 || bytes[1] !== 0x4D) {
        throw new Error('Databender: invalid BMP header after effect');
    }
    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const dataOffset = dv.getUint32(10, true);
    const w = dv.getInt32(18, true);
    const h = dv.getInt32(22, true);
    const absW = Math.abs(w);
    const absH = Math.abs(h);
    const topDown = h < 0; // negative height = top-down
    const pad = (4 - ((absW * 3) % 4)) % 4;
    const stride = absW * 3 + pad;

    const img = new ImageData(absW, absH);
    const d = img.data;

    for (let y = 0; y < absH; y++) {
        const srcY = topDown ? y : absH - 1 - y;
        const row = dataOffset + y * stride;
        for (let x = 0; x < absW; x++) {
            const p = row + x * 3;
            const oi = (srcY * absW + x) * 4;
            if (p + 2 < bytes.length) {
                d[oi] = bytes[p + 2];     // R
                d[oi + 1] = bytes[p + 1]; // G
                d[oi + 2] = bytes[p];     // B
            }
            d[oi + 3] = 255;
        }
    }

    return img;
}

// ---- param mapping ----

function buildParams(effect, args) {
    const params = {};
    const defaults = getDefaultParams(effect.id);
    effect.params.forEach((param, i) => {
        let val;
        if (i < args.length && args[i] !== undefined && args[i] !== null) {
            val = args[i];
        } else {
            val = defaults[param.id];
        }
        if (param.type === 'range') {
            val = Number(val);
        } else if (param.type === 'select') {
            // The knob sends an integer index — convert to the option string
            if (typeof val === 'number' || /^\d+$/.test(String(val))) {
                const idx = Math.round(Number(val));
                val = param.options[idx] ?? param.default;
            }
        }
        params[param.id] = val;
    });
    return params;
}

// ---- bridge ----

function createBridge(effect) {
    return async (canvas, ctx, ...args) => {
        const width = canvas.width;
        const height = canvas.height;
        const imageData = ctx.getImageData(0, 0, width, height);

        // Encode to BMP
        const { bytes, dataOffset, stride } = imageDataToBMP(imageData);
        const info = { width, height, dataOffset, stride };

        // Build params from positional args
        const params = buildParams(effect, args);
        const seed = (Math.random() * 0xFFFFFFFF) >>> 0;

        // Run the effect
        const resultBytes = effect.apply(bytes, info, params, seed);

        // Decode back — the result may have a different size (echo/move/carve
        // change the byte count), but the BMP header is preserved/updated by
        // the effect via the commit() pattern. Since our effects return raw
        // bytes with the original header intact (they only touch pixel data
        // or change total length), we need to handle size changes gracefully.
        try {
            const newImageData = bmpToImageData(resultBytes);
            // If dimensions changed, scale to fill the canvas
            if (newImageData.width !== width || newImageData.height !== height) {
                const tmpCanvas = document.createElement('canvas');
                tmpCanvas.width = newImageData.width;
                tmpCanvas.height = newImageData.height;
                tmpCanvas.getContext('2d').putImageData(newImageData, 0, 0);
                ctx.clearRect(0, 0, width, height);
                ctx.drawImage(tmpCanvas, 0, 0, width, height);
            } else {
                ctx.putImageData(newImageData, 0, 0);
            }
        } catch (e) {
            // If BMP parsing fails (extreme corruption), try to salvage
            // by reading the pixel data region directly with original dimensions
            ctx.putImageData(imageData, 0, 0);
        }
    };
}

// Build bridged functions for all Databender effects.
function buildBridgedEffects() {
    const bridged = {};
    for (const effect of EFFECTS) {
        bridged[effect.id] = createBridge(effect);
    }
    return bridged;
}

// Build fx.js-compatible descriptors from all Databender effects.
function buildFxDescriptors(bridgedEffects) {
    const descriptors = [];
    for (const effect of EFFECTS) {
        const inputs = effect.params.map(param => {
            if (param.type === 'select') {
                const defaultIdx = Math.max(0, param.options.indexOf(param.default));
                return {
                    name: param.label || param.id,
                    type: 'knob',
                    min: 0,
                    max: param.options.length - 1,
                    step: 1,
                    value: defaultIdx
                };
            }
            // range
            return {
                name: param.label || param.id,
                type: 'knob',
                min: param.min,
                max: param.max,
                step: param.step,
                value: param.default
            };
        });

        descriptors.push({
            name: 'db-' + effect.id,
            inputs,
            fn: bridgedEffects[effect.id]
        });
    }
    return descriptors;
}

module.exports = {
    buildBridgedEffects,
    buildFxDescriptors,
    createBridge,
    buildParams,
    getEffectIds,
    imageDataToBMP,
    bmpToImageData
};
